import React from "react";
import * as DropdownMenuReducedPrimitive from "@radix-ui/react-dropdown-menu";

import { cn } from "@/utils/utils";
import { ChevronDown } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";

const DropdownMenuReduced = DropdownMenuReducedPrimitive.Root;

interface DropdownMenuReducedTriggerProps
  extends React.ComponentPropsWithoutRef<
    typeof DropdownMenuReducedPrimitive.Trigger
  > {
  hideArrow?: boolean;
  customIcon?: JSX.Element;
  hideBorder?: boolean;
}

const DropdownMenuReducedTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuReducedPrimitive.Trigger>,
  DropdownMenuReducedTriggerProps
>(
  (
    {
      className,
      disabled,
      hideArrow,
      customIcon,
      hideBorder = false,
      ...props
    },
    ref
  ) => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    return (
      <DropdownMenuReducedPrimitive.Trigger
        ref={ref}
        className={cn(
          "h-full inline-flex items-center justify-between px-4 py-1 rounded-md transition-colors duration-200",
          "group outline-none gap-3 rounded-md px-1.5 py-2 transition-all min-w-40",
          {
            "border border-border-border2 data-[state=open]:border-foreground":
              !hideBorder,
            "hover:border-border-widget": !disabled,
            "cursor-not-allowed": disabled,
            "border-border-field-default text-border-field-default": disabled
          },
          className
        )}
        data-disabled={disabled}
        onPointerDown={handleClick}
        {...props}
      >
        {props.children}
        {!hideArrow && !customIcon && (
          <IconContainer
            size="5"
            color="secondary"
            className="transition-transform duration-200 group-data-[state=open]:rotate-180 group-data-[state=closed]:rotate-0"
          >
            <ChevronDown />
          </IconContainer>
        )}
        {customIcon && customIcon}
      </DropdownMenuReducedPrimitive.Trigger>
    );
  }
);
DropdownMenuReducedTrigger.displayName =
  DropdownMenuReducedPrimitive.Trigger.displayName;

const DropdownMenuReducedItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuReducedPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuReducedPrimitive.Item>
>(({ className, disabled, onClick, unselectable, ...props }, ref) => {
  return (
    <DropdownMenuReducedPrimitive.Item
      ref={ref}
      className={cn(
        "flex w-full justify-between items-center outline-none cursor-pointer rounded-sm px-3 py-1 text-base",
        className,
        {
          "hover:bg-surface-tooltip-toast": !disabled && unselectable === "off",
          "cursor-not-allowed": disabled,
          "border-border-field-default text-border-field-default": disabled,
          "text-tertiary cursor-auto hover:bg-auto": unselectable === "on"
        }
      )}
      data-disabled={disabled}
      onClick={disabled ? undefined : onClick}
      {...props}
    >
      {props.children}
    </DropdownMenuReducedPrimitive.Item>
  );
});
DropdownMenuReducedItem.displayName =
  DropdownMenuReducedPrimitive.Item.displayName;

const DropdownMenuReducedSeparator = DropdownMenuReducedPrimitive.Separator;

const DropdownMenuReducedContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuReducedPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuReducedPrimitive.Content>
>(({ className, sideOffset = 4, align = "start", ...props }, ref) => (
  <DropdownMenuReducedPrimitive.Content
    ref={ref}
    align={align}
    sideOffset={sideOffset}
    className={cn(
      "bg-surface-elevation2 z-50 border border-badges-default text-h3 rounded-md p-1 flex flex-col gap-2 w-40",
      "data-[state=open]:animate-fadeIn data-[state=closed]:animate-fadeOut",
      className
    )}
    {...props}
  >
    {props.children}
  </DropdownMenuReducedPrimitive.Content>
));
DropdownMenuReducedContent.displayName =
  DropdownMenuReducedPrimitive.Content.displayName;

export {
  DropdownMenuReduced,
  DropdownMenuReducedTrigger,
  DropdownMenuReducedContent,
  DropdownMenuReducedItem,
  DropdownMenuReducedSeparator
};
